import { create } from 'zustand';
import type { TimUnreadDetails } from './tim-bridge';

export type TimOpenSource = 'manual' | 'email' | 'apppush' | 'toast' | 'whatsapp' | 'sms';
export type TimUnreadCountsByOrderId = Record<string, number>;

export const parseTimOpenSource = (
  value?: string | null
): TimOpenSource => {
  if (value === 'manual' || value === 'email' || value === 'apppush' || value === 'whatsapp' || value === 'sms') {
    return value;
  }
  return 'manual';
};

export const isTimAutoOpenSource = (source: TimOpenSource) =>
  source !== 'manual' && source !== 'toast';

type OpenTimModalParams = {
  campaignId?: string | number | null;
  orderId?: string | number | null;
  source?: TimOpenSource;
};

type TimModalStore = {
  showTimModal: boolean;
  timUnreadCount: number;
  timUnreadCountsByOrderId: TimUnreadCountsByOrderId;
  currentCampaignId: string;
  currentOrderId: string;
  openSource: TimOpenSource;
  /** 每次打开请求递增，用于把相同 order 的二次点击当作新命令 */
  openSeq: number;
  /** iframe 已完成首次初始化（iframe 已挂载，src 不含 token） */
  timLoaded: boolean;
  /** iframe src（裸 URL，token 通过 SET_STATE AUTH 发送） */
  timUrl: string;
  setShowTimModal: (show: boolean, params?: OpenTimModalParams) => void;
  setTimUnreadCount: (count: number, details?: TimUnreadDetails) => void;
  /** 幂等：首次调用时写入 timUrl 并标记 timLoaded=true */
  setTimReady: (url: string) => void;
};

const toStoreValue = (value?: string | number | null) =>
  value == null ? '' : String(value);

const normalizeUnreadCount = (count: number) => Math.max(0, Number(count) || 0);

const buildOrderUnreadCounts = (details: TimUnreadDetails) => {
  const next: TimUnreadCountsByOrderId = {};
  for (const item of details.items || []) {
    const orderId = toStoreValue(item.orderId);
    const count = normalizeUnreadCount(item.count);
    if (!orderId || count <= 0) continue;
    next[orderId] = (next[orderId] || 0) + count;
  }
  return next;
};

const mergeOrderUnreadDetails = (
  current: TimUnreadCountsByOrderId,
  totalUnreadCount: number,
  details?: TimUnreadDetails
) => {
  if (!details || details.scope !== 'order_groups') {
    return totalUnreadCount <= 0 ? {} : current;
  }

  if (totalUnreadCount <= 0) {
    return details.complete ? {} : current;
  }

  const next = buildOrderUnreadCounts(details);
  return details.complete ? next : { ...current, ...next };
};

export const getTimUnreadCountByOrderId = (
  counts: TimUnreadCountsByOrderId,
  orderId?: string | number | null
) => {
  const key = toStoreValue(orderId);
  return key ? counts[key] || 0 : 0;
};

export const useTimModalStore = create<TimModalStore>()((set) => ({
  showTimModal: false,
  timUnreadCount: 0,
  timUnreadCountsByOrderId: {},
  currentCampaignId: '',
  currentOrderId: '',
  openSource: 'manual',
  openSeq: 0,
  timLoaded: false,
  timUrl: '',
  setShowTimModal: (show, params) =>
    set((state) => ({
      showTimModal: show,
      currentCampaignId: show ? toStoreValue(params?.campaignId) : '',
      currentOrderId: show ? toStoreValue(params?.orderId) : '',
      openSource: show ? params?.source || 'manual' : state.openSource,
      openSeq: show ? state.openSeq + 1 : state.openSeq
    })),
  setTimUnreadCount: (count, details) =>
    set((state) => {
      const nextCount = normalizeUnreadCount(count);
      return {
        timUnreadCount: nextCount,
        timUnreadCountsByOrderId: mergeOrderUnreadDetails(
          state.timUnreadCountsByOrderId,
          nextCount,
          details
        )
      };
    }),
  setTimReady: (url) =>
    set((state) => (state.timLoaded ? state : { timLoaded: true, timUrl: url }))
}));
